import { useState, useCallback, useEffect, useRef, useLayoutEffect } from 'react';
import { createFileRoute, redirect, useSearch, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { UserPlus, Monitor, ChevronLeft, ChevronRight, ChevronDown, Check } from 'lucide-react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { usePermissions } from '@/hooks/api/use-permissions';
import {
  useTeamMembers,
  useTeamInvitations,
  useRevokeInvitation,
  useResendInvitation,
} from '@/hooks/api/use-team';
import type { Invitation, TeamMember } from '@/hooks/api/use-team';
import { MemberTable } from '@/components/team/member-table';
import { InvitationTable } from '@/components/team/invitation-table';
import { TeamSearch } from '@/components/team/team-search';
import { TeamEmptyState } from '@/components/team/team-empty-state';
import { InviteModal } from '@/components/team/invite-modal';
import { DeactivationModal } from '@/components/team/deactivation-modal';
import { RoleChangeModal } from '@/components/team/role-change-modal';
import { ReactivationModal } from '@/components/team/reactivation-modal';
import { authClient, useSession } from '@/lib/auth-client';
import { api } from '@/lib/api-client';

type TabKey = 'active' | 'pending' | 'deactivated';

export const Route = createFileRoute('/_authenticated/team')({
  validateSearch: (search: Record<string, unknown>) => ({
    invite: search.invite === true,
  }),
  beforeLoad: async () => {
    const session = await authClient.getSession();
    if (!session.data) throw redirect({ to: '/' });

    try {
      const perms = await api.get<{ data: { role: string } }>('/permissions/me');
      const role = perms.data?.role;
      // OWNER, ADMIN, MEMBER can access /team (MEMBER = read-only)
      // LAWYER and unknown roles get redirected
      if (role !== 'OWNER' && role !== 'ADMIN' && role !== 'MEMBER') {
        throw redirect({ to: '/dashboard?teamDenied=true' as '/dashboard' });
      }
    } catch (e) {
      if (e && typeof e === 'object' && 'to' in e) throw e;
      throw redirect({ to: '/dashboard' });
    }
  },
  component: TeamPage,
});

function TeamPageSkeleton() {
  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <div className="flex items-center justify-between">
        <div className="h-8 w-40 animate-pulse rounded-md bg-muted" />
        <div className="h-9 w-32 animate-pulse rounded-md bg-muted" />
      </div>
      <div className="h-10 w-80 animate-pulse rounded-md bg-muted" />
      <div className="h-64 animate-pulse rounded-xl border bg-card" />
    </div>
  );
}

function TableSkeleton() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="flex items-center gap-4">
          <div className="size-8 animate-pulse rounded-full bg-muted" />
          <div className="h-4 flex-1 animate-pulse rounded bg-muted" />
          <div className="h-4 w-20 animate-pulse rounded bg-muted" />
          <div className="h-4 w-16 animate-pulse rounded bg-muted" />
        </div>
      ))}
    </div>
  );
}

// P4: Tier C — desktop-only message on mobile
function MobileMessage() {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-12 text-center lg:hidden">
      <div className="flex size-12 items-center justify-center rounded-full bg-muted">
        <Monitor className="size-6 text-muted-foreground" />
      </div>
      <p className="text-sm text-muted-foreground max-w-xs">{t('team.desktopOnly')}</p>
    </div>
  );
}

const PAGE_SIZES = [10, 25, 50];

// --- Filter Dropdown (portal-based, consistent with Contracts page) ---

function FilterDropdown({
  value,
  onChange,
  options,
  placeholder,
  showAllOption = true,
}: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  placeholder: string;
  showAllOption?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0, width: 0 });

  const selectedLabel = options.find((o) => o.value === value)?.label ?? placeholder;

  const close = useCallback(() => {
    setOpen(false);
    triggerRef.current?.focus();
  }, []);

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const menuHeight = (options.length + (showAllOption ? 1 : 0)) * 36 + 8;
    const spaceBelow = window.innerHeight - rect.bottom - 8;
    const fitsBelow = spaceBelow >= menuHeight;
    setMenuPos({
      top: fitsBelow ? rect.bottom + 4 : rect.top - menuHeight - 4,
      left: rect.left,
      width: Math.max(rect.width, 180),
    });
  }, [open, options.length, showAllOption]);

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(e.target as Node)
      )
        setOpen(false);
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') close();
    }
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open, close]);

  return (
    <div className="relative">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen(!open)}
        className="flex h-10 items-center gap-2 rounded-md border border-input bg-transparent px-3 text-sm transition-colors hover:bg-accent cursor-pointer focus-visible:outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={value ? 'text-foreground' : 'text-muted-foreground'}>{selectedLabel}</span>
        <ChevronDown className="size-4 text-muted-foreground" />
      </button>
      {open &&
        createPortal(
          <div
            ref={menuRef}
            className="fixed z-50 max-h-60 overflow-y-auto rounded-md border bg-popover py-1 shadow-md"
            style={{ top: menuPos.top, left: menuPos.left, minWidth: menuPos.width }}
            role="listbox"
          >
            {showAllOption && (
              <button
                type="button"
                role="option"
                aria-selected={value === ''}
                className="flex w-full items-center gap-2 px-3 py-2 text-sm hover:bg-accent cursor-pointer"
                onClick={() => {
                  onChange('');
                  close();
                }}
              >
                <Check className={`size-3.5 ${value === '' ? 'opacity-100' : 'opacity-0'}`} />
                {placeholder}
              </button>
            )}
            {options.map((opt) => (
              <button
                key={opt.value}
                type="button"
                role="option"
                aria-selected={value === opt.value}
                className="flex w-full items-center gap-2 px-3 py-2 text-sm hover:bg-accent cursor-pointer"
                onClick={() => {
                  onChange(opt.value);
                  close();
                }}
              >
                <Check
                  className={`size-3.5 ${value === opt.value ? 'opacity-100' : 'opacity-0'}`}
                />
                {opt.label}
              </button>
            ))}
          </div>,
          document.body,
        )}
    </div>
  );
}

// --- Pagination ---

function TeamPagination({
  page,
  totalPages,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
}: {
  page: number;
  totalPages: number;
  pageSize: number;
  total: number;
  onPageChange: (p: number) => void;
  onPageSizeChange: (s: number) => void;
}) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 pt-4 text-sm">
      <div className="flex items-center gap-2 text-muted-foreground">
        <span>{t('team.totalMembers', { count: total })}</span>
        <span className="text-muted-foreground/50">|</span>
        <FilterDropdown
          value={String(pageSize)}
          onChange={(v) => onPageSizeChange(Number(v))}
          placeholder=""
          showAllOption={false}
          options={PAGE_SIZES.map((size) => ({
            value: String(size),
            label: t('team.perPage', { count: size }),
          }))}
        />
      </div>

      <div className="flex items-center gap-1">
        <Button
          variant="outline"
          size="icon-sm"
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
          className="cursor-pointer"
          aria-label={t('team.prevPage')}
        >
          <ChevronLeft className="size-4" />
        </Button>
        <span className="px-3 text-muted-foreground">
          {t('team.pageOf', { page, totalPages: totalPages || 1 })}
        </span>
        <Button
          variant="outline"
          size="icon-sm"
          disabled={page >= totalPages}
          onClick={() => onPageChange(page + 1)}
          className="cursor-pointer"
          aria-label={t('team.nextPage')}
        >
          <ChevronRight className="size-4" />
        </Button>
      </div>
    </div>
  );
}

function TeamPage() {
  const { t } = useTranslation();
  const { data: permissions, isPending: permsPending } = usePermissions();
  const { data: session } = useSession();

  const { invite: inviteFromUrl } = useSearch({ from: '/_authenticated/team' });
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<TabKey>('active');
  const [search, setSearch] = useState('');

  // Per-tab pagination state
  const [activePage, setActivePage] = useState(1);
  const [activePageSize, setActivePageSize] = useState(10);
  const [pendingPage, setPendingPage] = useState(1);
  const [pendingPageSize, setPendingPageSize] = useState(10);
  const [deactivatedPage, setDeactivatedPage] = useState(1);
  const [deactivatedPageSize, setDeactivatedPageSize] = useState(10);

  // Modal state
  const [inviteOpen, setInviteOpen] = useState(false);

  // Auto-open invite modal when navigating with ?invite=true
  useEffect(() => {
    if (inviteFromUrl) {
      setInviteOpen(true);
      void navigate({ to: '/team', search: { invite: false }, replace: true });
    }
  }, [inviteFromUrl, navigate]);
  const [deactivateMember, setDeactivateMember] = useState<TeamMember | null>(null);
  const [roleChangeMember, setRoleChangeMember] = useState<TeamMember | null>(null);
  const [reactivateMember, setReactivateMember] = useState<TeamMember | null>(null);

  const actorRole = permissions?.role ?? '';
  const actorUserId = session?.user?.id ?? '';
  const canManageTeam = actorRole === 'OWNER' || actorRole === 'ADMIN';

  const searchFilter = search || undefined;

  // Fetch active members (always — needed for count)
  const { data: activeMembers, isPending: activePending } = useTeamMembers({
    status: 'active',
    search: searchFilter,
    page: activePage,
    pageSize: activePageSize,
  });

  // Fetch deactivated members (always — needed for count)
  const { data: deactivatedMembers, isPending: deactivatedPending } = useTeamMembers({
    status: 'deactivated',
    search: searchFilter,
    page: deactivatedPage,
    pageSize: deactivatedPageSize,
  });

  // Fetch all invitations (always — needed for count and email validation).
  // refetchInterval keeps data fresh when admin is on this page.
  const { data: allInvitations, isPending: invitationsPending } = useTeamInvitations(
    { search: searchFilter },
    { refetchInterval: 30_000 },
  );

  // Mutations
  const revokeInvitation = useRevokeInvitation();
  const resendInvitation = useResendInvitation();

  const handleSearch = useCallback((value: string) => {
    setSearch(value);
    setActivePage(1);
    setPendingPage(1);
    setDeactivatedPage(1);
  }, []);

  const handleRevoke = useCallback(
    (invitation: Invitation) => {
      revokeInvitation.mutate(invitation.id, {
        onSuccess: () => toast.success(t('team.revokeSuccess', { email: invitation.email })),
        onError: () => toast.error(t('team.revokeError')),
      });
    },
    [revokeInvitation, t],
  );

  const handleResend = useCallback(
    (invitation: Invitation) => {
      resendInvitation.mutate(invitation.id, {
        onSuccess: () => toast.success(t('team.resendSuccess', { email: invitation.email })),
        onError: () => toast.error(t('team.resendError')),
      });
    },
    [resendInvitation, t],
  );

  const handleDeactivate = useCallback((member: TeamMember) => {
    setDeactivateMember(member);
  }, []);

  const handleChangeRole = useCallback((member: TeamMember) => {
    setRoleChangeMember(member);
  }, []);

  const handleReactivate = useCallback((member: TeamMember) => {
    setReactivateMember(member);
  }, []);

  // Filter invitations for the "Oczekujące" tab:
  // 1. Hide accepted — they're now active members
  // 2. Hide revoked/rejected older than 24h — no longer actionable
  const TERMINAL_MAX_AGE_MS = 24 * 60 * 60 * 1000;
  const now = Date.now();
  const pendingInvitations =
    allInvitations?.data?.filter((inv) => {
      if (inv.status === 'accepted') return false;
      if (inv.status === 'revoked' || inv.status === 'rejected') {
        return now - new Date(inv.createdAt).getTime() < TERMINAL_MAX_AGE_MS;
      }
      return true;
    }) ?? [];
  // Emails with active (non-terminal) invitations — for duplicate validation in invite modal
  const existingInvitationEmails = (allInvitations?.data ?? [])
    .filter((inv) => inv.status === 'pending' || inv.status === 'accepted')
    .map((inv) => inv.email);

  // Client-side pagination for pending invitations
  const pendingTotal = pendingInvitations.length;
  const pendingTotalPages = Math.ceil(pendingTotal / pendingPageSize);
  const paginatedPendingInvitations = pendingInvitations.slice(
    (pendingPage - 1) * pendingPageSize,
    pendingPage * pendingPageSize,
  );

  // Counts for tabs (K4: always show real numbers)
  const activeCount = activeMembers?.total ?? 0;
  const pendingCount = pendingTotal;
  const deactivatedCount = deactivatedMembers?.total ?? 0;

  // Total pages for server-paginated tabs
  const activeTotalPages = Math.ceil(activeCount / activePageSize);
  const deactivatedTotalPages = Math.ceil(deactivatedCount / deactivatedPageSize);

  if (permsPending) {
    return <TeamPageSkeleton />;
  }

  const tabs: { key: TabKey; label: string; count: number }[] = canManageTeam
    ? [
        { key: 'active', label: t('team.tabActive'), count: activeCount },
        { key: 'pending', label: t('team.tabPending'), count: pendingCount },
        { key: 'deactivated', label: t('team.tabDeactivated'), count: deactivatedCount },
      ]
    : [{ key: 'active', label: t('team.tabActive'), count: activeCount }];

  return (
    <>
      {/* P4: Mobile — show message instead of table */}
      <MobileMessage />

      {/* Desktop content */}
      <div className="mx-auto hidden max-w-5xl lg:block">
        {/* Sticky header + search + tabs */}
        <div className="sticky top-0 z-10 -mt-6 space-y-6 bg-background pt-6 pb-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold tracking-tight">{t('team.title')}</h1>
            {canManageTeam && (
              <Button className="cursor-pointer" onClick={() => setInviteOpen(true)}>
                <UserPlus className="size-4" />
                {t('team.inviteButton')}
              </Button>
            )}
          </div>

          {/* Search — shared across all tabs, placeholder changes per tab */}
          <div className="max-w-sm">
            <TeamSearch
              value={search}
              onChange={handleSearch}
              placeholder={activeTab === 'pending' ? t('team.searchPlaceholderEmail') : undefined}
            />
          </div>

          {/* P2: Tabs with ARIA */}
          <div className="flex gap-1 border-b" role="tablist" aria-label={t('team.title')}>
            {tabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                role="tab"
                aria-selected={activeTab === tab.key}
                aria-controls={`tabpanel-${tab.key}`}
                id={`tab-${tab.key}`}
                className={`cursor-pointer px-4 py-2.5 text-sm font-medium transition-colors ${
                  activeTab === tab.key
                    ? 'border-b-2 border-primary text-primary'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.label}
                <span className="ml-2 rounded-full bg-muted px-2 py-0.5 text-xs">{tab.count}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Tab content */}
        <Card>
          <CardContent>
            {/* Active members */}
            <div
              id="tabpanel-active"
              role="tabpanel"
              aria-labelledby="tab-active"
              hidden={activeTab !== 'active'}
            >
              {activeTab === 'active' &&
                (activePending ? (
                  <TableSkeleton />
                ) : activeMembers?.data?.length ? (
                  <>
                    <MemberTable
                      members={activeMembers.data}
                      actorRole={actorRole}
                      actorUserId={actorUserId}
                      onDeactivate={handleDeactivate}
                      onChangeRole={handleChangeRole}
                    />
                    {activeCount > 0 && (
                      <TeamPagination
                        page={activePage}
                        totalPages={activeTotalPages}
                        pageSize={activePageSize}
                        total={activeCount}
                        onPageChange={setActivePage}
                        onPageSizeChange={(s) => {
                          setActivePageSize(s);
                          setActivePage(1);
                        }}
                      />
                    )}
                  </>
                ) : (
                  <TeamEmptyState variant="active" />
                ))}
            </div>

            {/* Pending invitations */}
            <div
              id="tabpanel-pending"
              role="tabpanel"
              aria-labelledby="tab-pending"
              hidden={activeTab !== 'pending'}
            >
              {activeTab === 'pending' &&
                (invitationsPending ? (
                  <TableSkeleton />
                ) : pendingInvitations.length ? (
                  <>
                    <InvitationTable
                      invitations={paginatedPendingInvitations}
                      onRevoke={handleRevoke}
                      onResend={handleResend}
                      revokingId={
                        revokeInvitation.isPending ? (revokeInvitation.variables ?? null) : null
                      }
                      resendingId={
                        resendInvitation.isPending ? (resendInvitation.variables ?? null) : null
                      }
                    />
                    {pendingTotal > 0 && (
                      <TeamPagination
                        page={pendingPage}
                        totalPages={pendingTotalPages}
                        pageSize={pendingPageSize}
                        total={pendingTotal}
                        onPageChange={setPendingPage}
                        onPageSizeChange={(s) => {
                          setPendingPageSize(s);
                          setPendingPage(1);
                        }}
                      />
                    )}
                  </>
                ) : (
                  <TeamEmptyState variant="pending" />
                ))}
            </div>

            {/* Deactivated members */}
            <div
              id="tabpanel-deactivated"
              role="tabpanel"
              aria-labelledby="tab-deactivated"
              hidden={activeTab !== 'deactivated'}
            >
              {activeTab === 'deactivated' &&
                (deactivatedPending ? (
                  <TableSkeleton />
                ) : deactivatedMembers?.data?.length ? (
                  <>
                    <MemberTable
                      members={deactivatedMembers.data}
                      actorRole={actorRole}
                      actorUserId={actorUserId}
                      onReactivate={handleReactivate}
                    />
                    {deactivatedCount > 0 && (
                      <TeamPagination
                        page={deactivatedPage}
                        totalPages={deactivatedTotalPages}
                        pageSize={deactivatedPageSize}
                        total={deactivatedCount}
                        onPageChange={setDeactivatedPage}
                        onPageSizeChange={(s) => {
                          setDeactivatedPageSize(s);
                          setDeactivatedPage(1);
                        }}
                      />
                    )}
                  </>
                ) : (
                  <TeamEmptyState variant="deactivated" />
                ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Modals */}
      <InviteModal
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        existingEmails={existingInvitationEmails}
      />
      <DeactivationModal
        open={!!deactivateMember}
        onOpenChange={(open) => !open && setDeactivateMember(null)}
        member={deactivateMember}
      />
      <RoleChangeModal
        open={!!roleChangeMember}
        onOpenChange={(open) => !open && setRoleChangeMember(null)}
        member={roleChangeMember}
      />
      <ReactivationModal
        open={!!reactivateMember}
        onOpenChange={(open) => !open && setReactivateMember(null)}
        member={reactivateMember}
      />
    </>
  );
}
